import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, ShieldCheck, BookOpen, ChevronLeft, AlertTriangle, Clock, XCircle, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { gregorianToHijri, formatHijri } from "@/lib/hijri";

function daysFromToday(g_iso) {
  if (!g_iso) return null;
  try {
    const [y, m, d] = g_iso.split("-").map(Number);
    const target = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.floor((target - today) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function statusOfDays(d) {
  if (d === null) return "missing";
  if (d < 0) return "expired";
  if (d <= 30) return "warning";
  return "valid";
}

function dayLabel(daysOffset, t) {
  if (daysOffset === 0) return t("today_label");
  if (daysOffset === 1) return t("tomorrow_label");
  return null;
}

export default function Calendar() {
  const { t, lang } = useT();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [emps, brs] = await Promise.all([api.get("/employees"), api.get("/branches")]);
        setEmployees(emps.data);
        setBranches(brs.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b])), [branches]);

  // Build day buckets: today .. today+30
  const buckets = useMemo(() => {
    const map = new Map(); // date_iso -> [{employee, cert, days}]
    employees.forEach((e) => {
      [
        { key_g: "health_expiry_g", key_h: "health_expiry_hijri", cert: "health" },
        { key_g: "training_expiry_g", key_h: "training_expiry_hijri", cert: "training" },
      ].forEach(({ key_g, key_h, cert }) => {
        const g = e[key_g];
        if (!g) return;
        const d = daysFromToday(g);
        if (d === null) return;
        // Show: expired (any), today, next 30 days
        if (d > 30) return;
        if (!map.has(g)) map.set(g, []);
        map.get(g).push({ e, cert, days: d, hijri: e[key_h] });
      });
    });
    // Sort entries
    const entries = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return entries;
  }, [employees]);

  const totalCount = useMemo(() => buckets.reduce((acc, [, list]) => acc + list.length, 0), [buckets]);

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-teal-900 flex items-center gap-2">
            <CalendarDays className="text-teal-600" size={22} /> {t("nav_calendar")}
          </h2>
          <p className="text-gray-500 text-sm">{t("upcoming_subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="pill status-expired"><XCircle size={12} /> {t("status_expired")}</span>
          <span className="pill status-warning"><Clock size={12} /> {t("status_warning")}</span>
          <span className="pill status-valid"><CheckCircle2 size={12} /> {t("status_valid")}</span>
        </div>
      </div>

      <div className="bg-white rounded-2xl card-shadow p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">{t("upcoming_expirations")}</p>
          <p className="text-2xl font-bold text-teal-800">{totalCount} <span className="text-sm font-normal text-gray-500">{t("employees_expiring")}</span></p>
        </div>
        <div className="text-amber-600">
          <AlertTriangle size={28} />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 py-20">{t("loading")}</div>
      ) : buckets.length === 0 ? (
        <div className="bg-white rounded-2xl card-shadow p-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 mb-4">
            <CheckCircle2 size={28} />
          </div>
          <p className="text-gray-700 font-semibold text-lg">{t("no_upcoming")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {buckets.map(([dateIso, items]) => {
            const days = daysFromToday(dateIso);
            const status = statusOfDays(days);
            const [y, m, d] = dateIso.split("-").map(Number);
            const hijri = gregorianToHijri(y, m, d);
            const labelOverride = dayLabel(days, t);
            const gDate = new Date(y, m - 1, d);
            const weekday = gDate.toLocaleDateString(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-US", { weekday: "long" });
            const gFmt = gDate.toLocaleDateString(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-US", { year: "numeric", month: "short", day: "numeric" });

            const ringClass =
              status === "expired" ? "border-r-4 border-rose-500" :
              days === 0 ? "border-r-4 border-teal-500" :
              status === "warning" ? "border-r-4 border-amber-500" :
              "border-r-4 border-gray-300";

            const headerBg =
              status === "expired" ? "bg-rose-50 text-rose-800" :
              days === 0 ? "bg-teal-50 text-teal-800" :
              status === "warning" ? "bg-amber-50 text-amber-800" :
              "bg-gray-50 text-gray-700";

            return (
              <div key={dateIso} className={`bg-white rounded-2xl card-shadow overflow-hidden ${ringClass}`} data-testid={`day-bucket-${dateIso}`}>
                <div className={`px-5 py-3 flex items-center justify-between ${headerBg}`}>
                  <div>
                    <div className="font-bold text-base">
                      {labelOverride ? <span className="mx-1">{labelOverride} · </span> : null}
                      {formatHijri(hijri)}هـ
                    </div>
                    <div className="text-xs opacity-80">{weekday} · <span style={{direction: "ltr"}}>{gFmt}</span></div>
                  </div>
                  <div className="text-end">
                    <div className="text-lg font-bold">{items.length}</div>
                    <div className="text-[11px] opacity-80">{t("employees_expiring")}</div>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {items.map(({ e, cert }, idx) => {
                    const b = branchMap[e.branch_id] || {};
                    const Icon = cert === "health" ? ShieldCheck : BookOpen;
                    const certColor = cert === "health" ? "text-emerald-700 bg-emerald-50" : "text-indigo-700 bg-indigo-50";
                    return (
                      <Link
                        key={`${e.id}-${cert}-${idx}`}
                        to={`/employees?q=${encodeURIComponent(e.name)}`}
                        className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition"
                        data-testid={`day-item-${dateIso}-${cert}-${e.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`rounded-lg p-1.5 ${certColor}`}>
                            <Icon size={14} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{e.name}</div>
                            <div className="text-xs text-gray-500 truncate">
                              {e.employee_id && <span>#{e.employee_id} · </span>}
                              {cert === "health" ? t("health_cert") : t("training_cert")}
                              {b.name && <span> · {b.code ? `[${b.code}] ` : ""}{b.name}</span>}
                            </div>
                          </div>
                        </div>
                        <ChevronLeft size={16} className="text-gray-300 flex-shrink-0 rtl:rotate-180" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
